import { useState, useRef, useEffect } from 'react';
import { Input } from './ui/input';
import { MapPin } from 'lucide-react';

/**
 * AddressAutocomplete component using Google Places API
 * @param {Object} props
 * @param {string} props.value - Current address value
 * @param {function} props.onChange - Callback when address changes (receives formatted string)
 * @param {function} props.onPlaceSelect - Callback when place is selected (receives {name, address, lat, lng})
 * @param {string} props.placeholder - Input placeholder
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.disabled - Whether input is disabled
 * @param {string} props.searchType - Type of search: 'address', 'establishment', or 'all' (default: 'all')
 */
const AddressAutocomplete = ({ 
  value = '', 
  onChange, 
  onPlaceSelect,
  onAddressSelect, // Legacy support
  placeholder = 'Enter an address',
  className = '',
  disabled = false,
  searchType = 'all'
}) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const onPlaceSelectRef = useRef(onPlaceSelect);
  const onAddressSelectRef = useRef(onAddressSelect);
  const [isLoaded, setIsLoaded] = useState(false);

  // Google owns the place_changed listener for the lifetime of the input. Keep
  // its callbacks current without rebuilding the autocomplete instance on every
  // parent render (or retaining callbacks that captured an old form draft).
  onChangeRef.current = onChange;
  onPlaceSelectRef.current = onPlaceSelect;
  onAddressSelectRef.current = onAddressSelect;

  // Check for Google Maps API and initialize
  useEffect(() => {
    let isMounted = true;
    
    const tryInit = () => {
      if (!isMounted) return;
      if (window.google?.maps?.places && inputRef.current && !autocompleteRef.current) {
        try {
          // Configure search types based on prop
          const autocompleteOptions = {
            componentRestrictions: { country: 'us' },
            fields: ['formatted_address', 'geometry', 'name', 'address_components', 'place_id', 'types']
          };
          
          // Set types based on searchType prop
          if (searchType === 'address') {
            autocompleteOptions.types = ['address'];
          } else if (searchType === 'establishment') {
            autocompleteOptions.types = ['establishment'];
          }
          // 'all' - don't restrict types, allows both addresses and places
          
          autocompleteRef.current = new window.google.maps.places.Autocomplete(
            inputRef.current, 
            autocompleteOptions
          );
          
          // Ensure the dropdown renders with high z-index
          // This is needed for modals/dialogs
          const pacContainerInterval = setInterval(() => {
            const pacContainer = document.querySelector('.pac-container');
            if (pacContainer) {
              pacContainer.style.zIndex = '99999';
              clearInterval(pacContainerInterval);
            }
          }, 100);
          setTimeout(() => clearInterval(pacContainerInterval), 5000);

          autocompleteRef.current.addListener('place_changed', () => {
            const place = autocompleteRef.current.getPlace();
            
            if (place) {
              const placeData = {
                name: place.name || '',
                address: place.formatted_address || '',
                formatted_address: place.formatted_address || '',
                lat: place.geometry?.location?.lat(),
                lng: place.geometry?.location?.lng(),
                place_id: place.place_id,
                types: place.types || [],
                components: {}
              };

              if (place.address_components) {
                place.address_components.forEach(component => {
                  const type = component.types[0];
                  placeData.components[type] = {
                    long: component.long_name,
                    short: component.short_name
                  };
                });
              }

              // For places/establishments, include name in the display
              // For plain addresses, just use the formatted address
              const isEstablishment = place.types?.some(t => 
                ['establishment', 'point_of_interest', 'park', 'stadium', 'gym'].includes(t)
              );
              
              let displayValue = place.formatted_address || '';
              if (isEstablishment && place.name && !place.formatted_address?.startsWith(place.name)) {
                displayValue = `${place.name}, ${place.formatted_address}`;
              }

              if (onChangeRef.current) {
                onChangeRef.current(displayValue);
              }

              if (onPlaceSelectRef.current) {
                onPlaceSelectRef.current(placeData);
              }
              
              // Legacy callback support
              if (onAddressSelectRef.current) {
                onAddressSelectRef.current(placeData);
              }
            }
          });

          if (isMounted) {
            setIsLoaded(true);
          }
        } catch (error) {
          console.error('Error initializing Google Places Autocomplete:', error);
        }
      }
    };

    // Try immediately, then poll if not ready
    tryInit();
    
    const checkInterval = setInterval(tryInit, 100);
    const timeout = setTimeout(() => {
      clearInterval(checkInterval);
    }, 10000);

    return () => {
      isMounted = false;
      clearInterval(checkInterval);
      clearTimeout(timeout);
    };
  }, [searchType]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (autocompleteRef.current) {
        window.google?.maps?.event?.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, []);

  const handleInputChange = (e) => {
    if (onChange) {
      onChange(e.target.value);
    }
  };

  return (
    <div className="relative">
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          placeholder={placeholder}
          disabled={disabled}
          className={`pl-10 ${className}`}
          data-testid="address-autocomplete-input"
        />
      </div>
      {!isLoaded && value && (
        <p className="text-xs text-gray-400 mt-1">
          Loading address suggestions...
        </p>
      )}
    </div>
  );
};

export default AddressAutocomplete;
